import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { userPermissionGuard, superAdminGuard } from 'app/core/permission';
import { LayoutComponent } from 'app/layout/layout.component';
import { ADMINISTRATION_PERMISSIONS } from 'app/modules/admin/administration-permissions';
import { SETTINGS_EMAIL_PERMISSIONS } from 'app/modules/settings/company-email/settings-email-permissions';
import { myHomePostLoginRedirect } from 'app/modules/my-home/my-home-post-login.redirect';

export const appRoutes: Route[] = [
    // Role-aware home: SuperAdmin → /my-home, others → accounting dashboard
    { path: '', pathMatch: 'full', redirectTo: 'signed-in-redirect' },
    {
        path: 'signed-in-redirect',
        pathMatch: 'full',
        canActivate: [myHomePostLoginRedirect],
        loadComponent: () =>
            import('app/modules/my-home/my-home-post-login-stub.component').then(
                (m) => m.MyHomePostLoginStubComponent
            ),
    },
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes')},
            {path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes')},
            {path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes')},
            {path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes')},
            {path: 'login/otp', loadChildren: () => import('app/modules/auth/browser-otp/browser-otp.routes')},
            {path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes')},
            {path: 'select-company', loadChildren: () => import('app/modules/auth/select-company/select-company.routes')},
            {path: 'select-branch', loadChildren: () => import('app/modules/auth/select-branch/select-branch.routes')}
        ]
    },
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes')},
            {path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes')},
            {
                path: 'account-statement-print',
                loadComponent: () =>
                    import(
                        'app/modules/accounting/reports/account-statement-print/account-statement-print.component'
                    ).then((m) => m.AccountStatementPrintComponent),
            },
            {
                path: 'purchases/purchase-orders/print/:id',
                loadComponent: () =>
                    import(
                        'app/modules/purchases/purchase-orders/purchase-order-print-document/purchase-order-print-document.component'
                    ).then((m) => m.PurchaseOrderPrintDocumentComponent),
            },
            {
                path: 'purchases/internal-purchase-orders/print/:id',
                loadComponent: () =>
                    import(
                        'app/modules/purchases/internal-purchase-orders/internal-purchase-order-print-document/internal-purchase-order-print-document.component'
                    ).then((m) => m.InternalPurchaseOrderPrintDocumentComponent),
            },
            {
                path: 'sales/sales-orders/print/:id',
                loadComponent: () =>
                    import(
                        'app/modules/sales/sales-orders/sales-order-print-document/sales-order-print-document.component'
                    ).then((m) => m.SalesOrderPrintDocumentComponent),
            },
        ]
    },

    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes')},
            {
                path: 'hr/sign/:token',
                loadComponent: () =>
                    import('app/modules/hr/public/public-sign-page.component').then(
                        (m) => m.PublicSignPageComponent
                    ),
            },
        ]
    },

    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'accounting', loadChildren: () => import('app/modules/accounting/accounting.routes')},
            {path: 'treasury', loadChildren: () => import('app/modules/accounting/treasury/treasury.routes')},
            {path: 'admin', loadChildren: () => import('app/modules/admin/admin-routes')},
            {path: 'settings', loadChildren: () => import('app/modules/settings/settings.routes')},
            {
                path: 'document-edit-requests',
                loadComponent: () =>
                    import('app/modules/admin/document-edit-requests/document-edit-requests-list.component').then(
                        (m) => m.DocumentEditRequestsListComponent
                    ),
            },
            {
                path: 'document-edit-requests/:id',
                loadComponent: () =>
                    import('app/modules/admin/document-edit-requests/document-edit-request-detail.component').then(
                        (m) => m.DocumentEditRequestDetailComponent
                    ),
            },
            {
                path: 'company-settings',
                canActivate: [userPermissionGuard],
                data: {
                    permission: [
                        ADMINISTRATION_PERMISSIONS.manageCompanySettings,
                        ADMINISTRATION_PERMISSIONS.managePostedDocumentEditSettings,
                        SETTINGS_EMAIL_PERMISSIONS.viewCompanyEmailSmtp,
                        SETTINGS_EMAIL_PERMISSIONS.manageCompanyEmailSmtp,
                    ],
                    permissionMode: 'any',
                    permissionFallback: '/accounting/dashboard',
                },
                loadComponent: () =>
                    import('app/modules/company-settings/company-settings.component').then(
                        (m) => m.CompanySettingsComponent
                    ),
            },
            {
                path: 'company-email',
                redirectTo: '/company-settings',
                pathMatch: 'full',
            },
            {
                path: 'activity-logs',
                canActivate: [userPermissionGuard],
                data: {
                    permission: ADMINISTRATION_PERMISSIONS.viewActivityLogs,
                    permissionFallback: '/accounting/dashboard',
                },
                loadComponent: () =>
                    import('app/modules/activity-logs/activity-logs.component').then(
                        (m) => m.ActivityLogsComponent
                    ),
            },
            {
                path: 'session-management',
                canActivate: [superAdminGuard],
                data: {
                    permissionFallback: '/accounting/dashboard',
                },
                loadComponent: () =>
                    import('app/modules/session-management/session-management.component').then(
                        (m) => m.SessionManagementComponent
                    ),
            },
            {
                path: 'my-home',
                loadChildren: () => import('app/modules/my-home/my-home.routes'),
            },
            {
                path: 'approvals/inbox',
                loadComponent: () =>
                    import(
                        'app/modules/approvals/unified-approval-inbox/unified-approval-inbox.component'
                    ).then((m) => m.UnifiedApprovalInboxComponent),
            },
            {path: 'inventory', loadChildren: () => import('app/modules/inventory/inventory.routes')},
            {path: 'procurement', loadChildren: () => import('app/modules/procurement/procurement.routes')},
            {
                path: 'trading',
                children: [
                    { path: '', pathMatch: 'full', redirectTo: '/inventory/trading-deals' },
                    { path: 'deals', pathMatch: 'full', redirectTo: '/inventory/trading-deals' },
                    { path: 'deals/add', pathMatch: 'full', redirectTo: '/inventory/trading-deals/add' },
                    { path: 'deals/edit/:id', redirectTo: '/inventory/trading-deals/edit/:id' },
                    { path: 'deals/detail/:id', redirectTo: '/inventory/trading-deals/detail/:id' },
                    { path: 'profitability', pathMatch: 'full', redirectTo: '/inventory/trading-deals/profitability' },
                ],
            },
            {path: 'ops', loadChildren: () => import('app/modules/ops/ops.routes')},
            {path: 'purchases', loadChildren: () => import('app/modules/purchases/purchases.routes')},
            {path: 'sales', loadChildren: () => import('app/modules/sales/sales.routes')},
            {path: 'commission', loadChildren: () => import('app/modules/commission/commission.routes')},
            {path: 'service-billing', loadChildren: () => import('app/modules/service-billing/service-billing.routes')},
            {path: 'digital-invoicing', loadChildren: () => import('app/modules/digital-invoicing/digital-invoicing.routes')},
            {path: 'commodity', loadChildren: () => import('app/modules/commodity/commodity.routes')},
            {path: 'manufacturing', loadChildren: () => import('app/modules/manufacturing/manufacturing.routes')},
            {path: 'hr', loadChildren: () => import('app/modules/hr/hr.routes')},
            {path: 'ess', loadChildren: () => import('app/modules/ess/ess.routes')},
            {path: 'logistics', loadChildren: () => import('app/modules/logistics/logistics.routes')},
            {path: 'gate', loadChildren: () => import('app/modules/gate/gate.routes')},
            {path: 'consignment', loadChildren: () => import('app/modules/consignment/consignment.routes')},
            {path: 'consignment', loadChildren: () => import('app/modules/consignment/consignment.routes')},
            {path: 'checklists', loadChildren: () => import('app/modules/checklists/checklists.routes')},
            {path: 'quality', loadChildren: () => import('app/modules/quality/quality.routes')},
            {path: 'support', loadChildren: () => import('app/modules/support/support.routes')},
            {path: 'support-center', loadChildren: () => import('app/modules/support-center/support-center.routes')},
            {path: 'pos', loadChildren: () => import('app/modules/pos/pos.routes')},
            {path: 'hospitality', loadChildren: () => import('app/modules/hospitality/hospitality.routes')},
            {path: 'example', loadChildren: () => import('app/modules/admin/example/example.routes')},
        ]
    }
];
